import React, { useMemo, useState } from 'react';
import { useAppSelector } from 'hooks';
import { Card, CardHeader, Col, Collapse, Row } from 'reactstrap';
import { skipToken } from '@reduxjs/toolkit/query/react';

import dayjs, { dateTimeDisplayFormat } from '~libs/dayjs';

import ClickableIcon from '~components/ClickableIcon';
import Icon, { faChevronDown, faChevronUp, faEye, faX } from '~components/icon';

import { Detail, ItemChange, SectionChange } from '~support-at-home/models';

import { useGetLogsQuery } from '~support-at-home/actions/budgets';

import { selectAppointmentServiceTypes } from '~appointments/selectors/serviceTypes';
import { selectPricePlanRevisions } from '~support-at-home/selectors/pricePlanRevisions';

interface ComponentProps {
  id?: string;
}

const BudgetActivityLog: React.FC<ComponentProps> = ({ id }) => {
  const revisions = useAppSelector(selectPricePlanRevisions);
  const serviceTypes = useAppSelector(selectAppointmentServiceTypes);
  const [isOpen, setIsOpen] = useState(false);

  const { data: logs } = useGetLogsQuery(id ? { id } : skipToken);

  const checkChange = (detail: Detail) => {
    const field = (detail.field || '').toLowerCase();
    return !(field === 'updated at' || field === 'created at' || field === 'updatedby');
  };

  // Temp formatting until backend sends formatted data
  const formatField = (change: Detail) => {
    const { field, from, to } = change;
    const getRevision = (id: string) => revisions.find((rev) => rev.id === id)?.sequence;

    switch (field) {
      case 'Price Plan Revision':
        return {
          field,
          from: from ? `v${getRevision(from)}` : null,
          to: `v${getRevision(to)}`,
        };
      case 'Contrib Proportion':
        return {
          field: 'Co-Contribution',
          from: from ? `${from}%` : null,
          to: `${to}%`,
        };
      default:
        return change;
    }
  };

  const createRowLink = (created_at: string, service_type_id: number, item_id?: string) => {
    const displayName = serviceTypes.find(({ id }) => id === service_type_id)?.name || 'Unknown';

    const handleHighlight = () => {
      const rowElement = document.querySelector(`tr[data-id="${item_id}"]`);
      if (!rowElement) return;

      rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      rowElement.classList.add('highlight');
      setTimeout(() => rowElement.classList.remove('highlight'), 2000);
    };

    const elementExists = !!document.querySelector(`tr[data-id="${item_id}"]`);

    return (
      <div className="d-flex justify-content-start align-items-center">
        <span>{displayName}</span>
        <ClickableIcon
          id={`activity-log-${service_type_id}-${created_at}`}
          icon={elementExists ? faEye : faX}
          onClick={handleHighlight}
          toolTip="Click to view row"
          disabled={!elementExists}
          className="ms-1"
          size="small"
        />
      </div>
    );
  };

  const buildChange = (change: Detail, key: string) => {
    if (!checkChange(change)) return null;
    const { field, from, to } = formatField(change);
    const changeText = from ? `"${from}" to "${to}"` : `Set to "${to}"`;

    return (
      <Row key={key}>
        <Col xs={2} className="text-end me-2">
          {field}:
        </Col>
        <Col>
          <span>{changeText}</span>
        </Col>
      </Row>
    );
  };

  const buildBudgetChanges = (budgetChanges: Detail[]) => (
    <Row className="mb-2">
      <strong>Budget Changes</strong>
      <Row className="ms-4">{budgetChanges.map((change, idx) => buildChange(change, `budget-${idx}`))}</Row>
    </Row>
  );

  const buildSectionChanges = (sections: SectionChange[]) => (
    <Row>
      <strong>Section Changes</strong>
      <Col>
        {sections.map(({ section_id, category, details }) => (
          <Row className="mb-2" key={section_id}>
            <Row sm={3}>{category.name}</Row>
            <Row className="ms-4">
              {details.map((detail, idx) => buildChange(detail, `${section_id}-detail-${idx}`))}
            </Row>
          </Row>
        ))}
      </Col>
    </Row>
  );

  const buildItemChanges = (items: ItemChange[], created_at: string) => (
    <Row>
      <strong>Item Changes</strong>
      <Col>
        {items.map(({ item_id, service_type_id, details }) => (
          <Row className="mb-2" key={item_id}>
            <Row sm={3}>{createRowLink(created_at, service_type_id, item_id)}</Row>
            <Row className="ms-4">{details.map((detail, idx) => buildChange(detail, `${item_id}-detail-${idx}`))}</Row>
          </Row>
        ))}
      </Col>
    </Row>
  );

  const builtLogs = useMemo(() => {
    return logs?.map(({ created_at, external_reference_user_name, changes }) => {
      const { budget, sections, items } = changes;

      return (
        <Row key={created_at} className="mb-2 w-100">
          <Col sm={3}>
            <strong>{dayjs(created_at).format(dateTimeDisplayFormat)}</strong> -{' '}
            {external_reference_user_name ?? 'System'}
          </Col>
          <Col sm={7}>
            {budget?.length > 0 && buildBudgetChanges(budget)}
            {sections?.length > 0 && buildSectionChanges(sections)}
            {items?.length > 0 && buildItemChanges(items, created_at)}
          </Col>
        </Row>
      );
    });
  }, [logs, revisions, serviceTypes]);

  return (
    <Card>
      <CardHeader
        className="d-flex justify-content-between border-0 fw-bold"
        type="button"
        onClick={() => setIsOpen((open) => !open)}
      >
        <div>Activity Log</div>
        <Icon icon={isOpen ? faChevronUp : faChevronDown} />
      </CardHeader>
      <Collapse isOpen={isOpen}>
        <div className="p-2">{logs && builtLogs}</div>
      </Collapse>
    </Card>
  );
};

export default BudgetActivityLog;
